/* eslint-disable @typescript-eslint/no-explicit-any */
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import { remove, uploadData } from 'aws-amplify/storage';
import { AuthSession, fetchAuthSession } from 'aws-amplify/auth';
import { InvokeParams, TeamConfigResponse } from '../types/data';
import { DynamoDBClient, PutItemCommand, ScanCommand, DeleteItemCommand } from '@aws-sdk/client-dynamodb';
import type { DynamoDbTask } from 'amplify/data/resource';
import { sourceTypes, structuredDataExtensions } from './constants';
import { UserType } from '@aws-sdk/client-cognito-identity-provider';
import logger from './logger';
import outputs from '../../amplify_outputs.json';
import type { UserAttributtes } from '@/context/userContext';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let globalSession: AuthSession | null = null;

export type AccessibleUserAttributes = keyof ReturnType<typeof getAmplifyOutputs>['custom']['userAttributes'];

export const getAmplifyOutputs = () => {
  if (outputs) {
    return outputs;
  } else {
    throw new Error('Amplify outputs not found');
  }
};

export const getGlobalAdminName = () => {
  return (import.meta.env.VITE_GLOBAL_ADMIN_GROUP as string) || '';
};

export const initializeSession = async (verbose: boolean = false) => {
  if (globalSession) {
    return;
  }

  if (verbose) {
    console.log('Initializing user session...');
  }
  globalSession = await fetchAuthSession();
  if (verbose) {
    console.log('User session initialized:', globalSession);
  }
};

export const getSession = async () => {
  if (!globalSession) {
    await initializeSession();
  }
  return globalSession as AuthSession;
};

export const resetSession = () => {
  globalSession = null;
};

export const getTeamConfigFromLambda = async (
  teamName: string,
  verbose: boolean = false
): Promise<TeamConfigResponse> => {
  const lambdaArn = import.meta.env.VITE_LAMBDA_FC_ARN || '';
  const lambdaFunctionName = lambdaArn.split(':').pop();

  const result: TeamConfigResponse = {
    q_business_app_url: '',
    q_business_s3_key: '',
    aws_account_id: '',
    dynamodb_table_name: '',
    q_business_s3_bucket: '',
    q_business_application_id: '',
    executionRoleArn: '',
  };

  if (!globalSession) {
    await initializeSession();
  }

  const lambdaClient = new LambdaClient({
    region: 'eu-central-1',
    credentials: globalSession!.credentials,
  });

  if (verbose) {
    console.log('Getting team config from Lambda for ', teamName);
  }

  const invokeParams: InvokeParams = {
    FunctionName: lambdaFunctionName,
    InvocationType: 'RequestResponse',
    Payload: JSON.stringify({
      team_name: teamName,
    }),
  };

  if (verbose) {
    console.log('Invoking Lambda with parameters:', invokeParams);
  }

  const response = await lambdaClient.send(new InvokeCommand(invokeParams));

  if (response.Payload) {
    const payloadString = new TextDecoder('utf-8').decode(response.Payload);
    const parsedPayload = JSON.parse(payloadString);
    const body = parsedPayload.body ? JSON.parse(parsedPayload.body) : {};

    Object.assign(result, {
      q_business_app_url: body.q_business_app_url || '',
      q_business_s3_key: body.q_business_s3_key || '',
      aws_account_id: body.aws_account_id || '',
      dynamodb_table_name: body.dynamodb_table_name || '',
      q_business_s3_bucket: body.q_business_s3_bucket || '',
      q_business_application_id: body.q_business_application_id || '',
      executionRoleArn: body.executionRoleArn || '',
    });
    if (verbose) {
      console.log('Lambda function output:', body);
    }
    return result;
  } else {
    if (verbose) {
      console.log('No payload received from Lambda.');
    }
    return result;
  }
};

export const fetchUserGroupInfo = async () => {
  const resp = {
    teamName: '',
    isGroupAdmin: false,
    isGlobalAdmin: false,
  };

  if (!globalSession) {
    await initializeSession();
  }

  try {
    // Extract user's group
    const usergroup = globalSession!.tokens?.accessToken?.payload?.['cognito:groups'] as string[] | undefined;

    if (!globalSession?.credentials) {
      throw new Error('No AWS credentials available from Amplify session.');
    }

    if (Array.isArray(usergroup)) {
      resp.teamName = usergroup.find((groupName) => !groupName.toLowerCase().includes('groupadmin')) || '';
      resp.isGroupAdmin = usergroup.some((groupName) => groupName.toLowerCase().includes('groupadmin'));
      resp.isGlobalAdmin = usergroup.some((groupName) => groupName.toLowerCase().includes(getGlobalAdminName()));
    }

    return resp;
  } catch (error) {
    logger.error('Error fetching user group information:', error);
    return resp;
  }
};

// reads the custom user attributes from the amplify outputs and sets the user attributes if they are present in the idToken
export const getUserAttributes = async () => {
  if (!globalSession) {
    await initializeSession();
  }

  const resp: UserAttributtes = {} as UserAttributtes;
  const ouputs = getAmplifyOutputs();
  const userAttributeskey = Object.keys(ouputs.custom.userAttributes);

  userAttributeskey.forEach((key) => {
    resp[key as AccessibleUserAttributes] = (globalSession!.tokens?.idToken?.payload[key] as string) || '';
  });

  return resp;
};

const loadDynamoDBClientConfig = async () => {
  const { credentials } = await getSession();
  const { teamName } = await fetchUserGroupInfo();
  const teamConfig: TeamConfigResponse = await getTeamConfigFromLambda(teamName);
  const region = 'eu-central-1';

  const dynamoDB = new DynamoDBClient({
    region: region,
    credentials,
  });

  const tableName = `arn:aws:dynamodb:${region}:${teamConfig.aws_account_id}:table/${teamConfig.dynamodb_table_name}`;

  return { dynamoDB, tableName };
};

export const handleS3Upload = async (file: File) => {
  let success: boolean = false;
  const { teamName } = await fetchUserGroupInfo();
  const teamConfig: TeamConfigResponse = await getTeamConfigFromLambda(teamName);
  const key = `${teamConfig.q_business_s3_key}/${file.name}`;

  try {
    const contentType = file.type || 'application/octet-stream';
    const result = await uploadData({
      path: key,
      data: file,
      options: {
        contentType: contentType,
        onProgress: ({ transferredBytes, totalBytes }) => {
          if (totalBytes) {
            console.log(`Upload progress ${Math.round((transferredBytes / totalBytes) * 100)} %`);
          }
        },

        bucket: {
          bucketName: teamConfig.q_business_s3_bucket,
          region: 'eu-central-1',
        },
      },
    }).result;
    if (result) {
      success = true;
    }
  } catch (error) {
    console.error('Error uploading file', error);

    return success;
  }
  return success;
};

export const handleS3Delete = async (key: string, verbose: boolean = false) => {
  let result = null;
  const { teamName } = await fetchUserGroupInfo();
  const teamConfig: TeamConfigResponse = await getTeamConfigFromLambda(teamName);

  if (!globalSession) await initializeSession();

  try {
    result = await remove({
      path: key,
      options: {
        bucket: {
          bucketName: teamConfig.q_business_s3_bucket,
          region: 'eu-central-1',
        },
      },
    });
  } catch (error) {
    if (verbose) {
      console.error('Error deleting file', error);
    }
    return result;
  }
  return result;
};

export const uplodaDynamoDb = async (username: string, taskStatus: string, selectedFile: File) => {
  const { dynamoDB, tableName } = await loadDynamoDBClientConfig();
  const sourceType = structuredDataExtensions.some((extension) => selectedFile.name.endsWith(extension))
    ? sourceTypes.structured
    : sourceTypes.unstructured;

  const params = {
    TableName: tableName,
    Item: {
      id: { S: selectedFile.name! },
      user: { S: username! },
      startTime: { S: new Date().toLocaleString('de-DE') },
      status: { S: taskStatus! },
      srcFile: { S: selectedFile.name! },
      sourceType: { S: sourceType },
    },
  };

  const command = new PutItemCommand(params);
  await dynamoDB.send(command);
};

export const getDynamoDbTasks = async () => {
  const { dynamoDB, tableName } = await loadDynamoDBClientConfig();

  const params = {
    TableName: tableName,
  };

  try {
    const command = new ScanCommand(params);
    const result = await dynamoDB.send(command);

    // Format the result to be a key value pair, since the result data are nested objects
    const formattedResult = result?.Items?.map((item) => {
      const formattedItem = {};

      // extract the key and value from the nested object and assign it to the formattedItem
      Object.entries(item).forEach(([key, value]) => {
        const simpleValue = Object.values(value)[0];

        Object.assign(formattedItem, {
          [key]: simpleValue,
        });
      });

      return formattedItem as DynamoDbTask;
    });

    return formattedResult;
  } catch (error) {
    console.error('Error fetching tasks:', error);
    return [];
  }
};

export const deleteTaskFromDynamoDb = async (id: string) => {
  const { dynamoDB, tableName } = await loadDynamoDBClientConfig();

  const params = {
    TableName: tableName,
    Key: {
      id: { S: id },
    },
  };

  const command = new DeleteItemCommand(params);
  await dynamoDB.send(command);
};

export const validateUsermail = (userMail: string) => {
  const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  return emailRegex.test(userMail);
};

export const extracEmailFromUser = (user: UserType | undefined) => {
  if (!user) {
    return '';
  }
  const email = user?.Attributes?.find((attr) => attr.Name === 'email')?.Value;
  return email || '';
};
